import React, { PropsWithChildren, useContext } from "react";
import { useAlbelliContext } from "../albelli/context";
import { CartLineItem } from "../cart/cart";
import { useCartContext } from "../cart/context";
import { useLogoUrlContext } from "../logo/context";
import { useWorkContext } from "../work/context";
import { Category, Product } from "./product";

export type ProductContext = {
  product: Product | undefined;
};

export const productContext = React.createContext<ProductContext | undefined>(
  undefined,
);

export type Props = {
  categories?: Category[] | undefined;
};

export function ProductContextProvider(props: PropsWithChildren<Props>) {
  const workContext = useWorkContext();
  const albelliContext = useAlbelliContext();
  const logoUrlContext = useLogoUrlContext();
  const cartContext = useCartContext();

  const workId = workContext.work?.workId;
  const albelliDocId = albelliContext.albelliDocument?.documentId;
  const logoId = logoUrlContext.logoId;

  if (!workId && !albelliDocId && !logoId) {
    return (
      <productContext.Provider value={{ product: undefined }}>
        {props.children}
      </productContext.Provider>
    );
  }

  // TODO: what if workId or albelliDocId or both are also present?

  if (!cartContext.cartLineItems) {
    // TODO: is this the right thing to do?
    //   Can we use some of the new React features to let React continue rendering children?
    return <></>;
  }

  // TODO: we should probably consider the (unlikely) case where
  //   both workId and albelliDocId have a value.
  //   At the very least we could add some logging, but we should
  //   also make sure that the product and content sources are consistent
  //   (i.e., we should not use product information from the work and
  //   content information from the albelli doc or vice-versa).

  const lineItem = findLineItemWithMatchingId(
    cartContext.cartLineItems,
    workId,
    albelliDocId,
    logoId,
  );

  if (!lineItem) {
    throw new Error("Could not find a matching line item");
  }

  const mpvId = logoUrlContext.logoId
    ? "logomakerPremiumIcon"
    : lineItem?.customOrderData.analytics?.product_id ||
      albelliContext.albelliDocument?.mpv;

  if (!mpvId) {
    throw new Error("Could not retrieve the MPV id");
  }

  return (
    <productContext.Provider
      value={
        lineItem
          ? {
              product: {
                mpvId: mpvId,
                coreProduct: {
                  key: lineItem.productData.productKey,
                  version: lineItem.productData.productVersion,
                  options: buildProductOptions(
                    lineItem.productData.selectedAttributes,
                  ),
                },
                presentation: {
                  productName: lineItem.merchandisingData.productName,
                  previewUrl:
                    logoUrlContext.logoUrl ||
                    lineItem.designData?.livePreviewUrl,
                },
                categories: props.categories,
                quantity: lineItem.quantity,
              },
            }
          : { product: undefined }
      }
    >
      {props.children}
    </productContext.Provider>
  );
}

export function useProductContext() {
  const context = useContext(productContext);
  if (!context) {
    throw new Error(
      "useProductContext must be used with ProductContextProvider",
    );
  }
  return context;
}

function findLineItemWithMatchingId(
  lineItems: CartLineItem[],
  workId: string | undefined,
  albelliDocId: string | undefined,
  logoId: string | undefined,
) {
  return lineItems.find(
    (item) =>
      (!!workId && item.customOrderData.analytics?.workId === workId) ||
      (!!albelliDocId &&
        item.customOrderData.analytics?.albelliDocId === albelliDocId) ||
      (!!logoId && item.customOrderData.analytics?.logoId === logoId),
  );
}

function buildProductOptions(
  selectedAttributes: {
    value: string;
    key: string;
  }[],
) {
  return selectedAttributes.reduce(
    (options, currentAttribute) => {
      options[currentAttribute.key] = currentAttribute.value;
      return options;
    },
    {} as Record<string, string>,
  );
}
